/* eslint-disable max-lines */
import {
  PM_PROVIDERS,
  SEARCH_DATE_FORMAT,
  TEAM_TYPES,
  TRACKING
} from 'common/constants/constants';
import { getPulseData } from 'common/services/graphql.service';
import * as TrackingService from 'common/services/tracking.service';
import _ from 'lodash';
import debounce from 'lodash/debounce';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';
import { getColor } from 'common/constants/_colors';
import {
  getIssuePossibleStates,
  patchIssueState
} from 'common/services/pm-issues.service';
import {
  DefinitionLevels,
  SettingValues
} from 'common/services/settings.service';
import MultipleAvatars from 'components/MultipleAvatars';
import Text from 'components/base/Text';
import useSettingsService from 'components/hooks/useSettingsService';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import ActivityCol from './components/ActivityCol';
import CodeChangesCol from './components/CodeChangesCol';
import IssueCol from './components/IssueCol';
import IterationGraph from './components/IterationGraph/IterationGraph';
import StuckCol from './components/StuckCol';
import useCurrentTeamUpdater from './hooks/useCurrentTeamUpdater';
import usePulseFilters from './hooks/usePulseFilters';
import {
  PULSE_EMPTY_STATES,
  mapIssueStatesToBranchStates
} from './pulse.common';
import {
  getIssueTypeIconByPmProvider,
  getIterationAccordingToType,
  getTopBarData,
  mapDataToUiPeopleFilterData,
  mapDataToUiTableData
} from './pulse.service';
import usePulseConfigSetting from './hooks/usePulseConfigSetting';
import {
  getLatestIterationSelector,
  selectedIterationPeriodSelector
} from 'state/selectors/iterations-picker';
import { checkIfIterationIsTooLong } from 'common/utils/utils';
import {
  individualContributorsSelector,
  teamContributorsSelector
} from 'state/selectors/contributors';
import {
  teamPmEntitiesIdsSelector,
  teamProjectSelector
} from 'state/selectors/projects';
import {
  isProjectManagementConnectedSelector,
  pmIntegrationSelector
} from 'state/selectors/sensors-authorizations';

const doneRowStyles = { backgroundColor: getColor('pastelGreen', 0.1) };

export const StyledSvgIcon = styled.img`
  width: 1.6rem;
  height: 1.6rem;
`;

// eslint-disable-next-line max-lines-per-function
const withPulseData = WrappedComponent => props => {
  const { currentTeam, toggleSnackBar, controlledIteration = null } = props;
  const pmSensor = useSelector(pmIntegrationSelector);
  const contributors = useSelector(individualContributorsSelector);
  const selectedTeamProject = useSelector(state =>
    teamProjectSelector(state, currentTeam?.id)
  );
  const teamPMEntitiesIds = useSelector(state =>
    teamPmEntitiesIdsSelector(state, currentTeam?.id)
  );
  const selectedTeamContributors = useSelector(state =>
    teamContributorsSelector(state, currentTeam?.id)
  );
  const isProjectManagementConnected = useSelector(
    isProjectManagementConnectedSelector
  );
  const history = useHistory();
  const [rawData, setRawData] = useState({});
  const [tableHeight, setTableHeight] = useState('0');
  const [branchesData, setBranchesData] = useState({ unmatched: [], all: [] });
  const [tableData, setTableData] = useState([]);
  const [selectedContributorIds, setSelectedContributorIds] = useState([]);
  const [topBarData, setTopBarData] = useState({});
  const [selectedIssueDropdownState, setSelectedIssueDropdownState] = useState(
    {}
  );
  const selectedIteration = useSelector(selectedIterationPeriodSelector);
  const [isLoading, setIsLoading] = useState(true);
  const [allowAnimations, setAllowAnimations] = useState(false);
  const { updateSetting, getTeamOrOrgSetting, getSettingOrDefinitionValue } =
    useSettingsService();

  const latestIteration = useSelector(getLatestIterationSelector) || {};

  const currentIteration =
    controlledIteration || selectedIteration || latestIteration;

  const { id: currentIterationId } = currentIteration;
  const currentTeamId = (currentTeam || {}).id;

  const teamPMConfigurationEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.teamPMConfigurationEnabled
  });

  const pulseConfigSetting = usePulseConfigSetting();

  const isPreventTooLongIterationsEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isPreventTooLongIterationsEnabled
  });

  const isGroup =
    currentTeam?.isParent ||
    currentTeam?.type === TEAM_TYPES.GROUP ||
    currentTeam?.type === TEAM_TYPES.ALL_TEAMS;

  const isAllContributors = currentTeam?.type === TEAM_TYPES.ALL_CONTRIBUTORS;

  const isPostRouteSetter =
    !!currentIteration.hasOwnProperty('isPostRouteSetter');

  const getNotConnectedType = () => {
    if (!currentTeam || isGroup || isAllContributors) {
      return PULSE_EMPTY_STATES.NO_TEAM_SELECTED;
    }
    if (!isProjectManagementConnected) {
      return PULSE_EMPTY_STATES.NO_PM_CONNECTED;
    }
    if (
      checkIfIterationIsTooLong({
        iteration: currentIteration,
        isPreventTooLongIterationsEnabled
      }) ||
      !currentIteration
    ) {
      return PULSE_EMPTY_STATES.TOO_LONG_ITERATION;
    }
    const isConfigured = !_.isEmpty(teamPMEntitiesIds);
    return isConfigured ? null : PULSE_EMPTY_STATES.UNCONFIGURED_TEAM;
  };

  const notConnectedType = getNotConnectedType();

  const workingDays = getTeamOrOrgSetting({
    name: SettingValues.WORKING_DAYS,
    definitionLevel: DefinitionLevels.TEAM,
    teamId: _.get(currentTeam, 'id')
  });

  const [
    issueStateFilterValues,
    issueTypeFilterValues,
    searchFilterInitValue,
    peopleFilterData,
    setPeopleFilterData,
    filterData,
    filterDataWithoutContributors,
    setIssueTypeFilterValues,
    setSearchFilterValue,
    setSearchFilterInitValue,
    setIssueStateFilterValues,
    searchFilterValue
  ] = usePulseFilters({
    selectedContributorIds,
    history,
    selectedTeamContributors,
    rawData
  });

  useCurrentTeamUpdater({
    currentTeam,
    getSettingOrDefinitionValue,
    updateSetting,
    workingDays,
    selectedContributorIds,
    setSelectedContributorIds
  });

  useEffect(() => {
    const { unmatched = [], all = [] } = rawData;
    setBranchesData({ unmatched, all });
    const tableData = mapDataToUiTableData(
      rawData,
      contributors,
      selectedContributorIds
    );
    setTableData([...tableData]);
  }, [rawData, contributors, selectedContributorIds]);

  // Debouncing the API call so we make the call after the selected iteration is changed
  // this way the pulse page header won't flicker and won't show inconsistent data
  const makeApiCall = debounce(() => {
    const shouldMakeApiCall =
      currentIterationId &&
      !notConnectedType &&
      isPostRouteSetter &&
      currentTeamId;

    if (shouldMakeApiCall) {
      const fetchDataSequentially = async () => {
        setIsLoading(true);
        const updatedIteration = getIterationAccordingToType(currentIteration);
        const data = { unmatched: [], all: [], issues: [] };

        try {
          const commonParams = {
            iteration: updatedIteration,
            team: currentTeam,
            sensor_id: pmSensor.sensor_id,
            timelineStartDate: moment()
              .subtract(14, 'day')
              .format(SEARCH_DATE_FORMAT),
            projectId: _.get(selectedTeamProject, 'id'),
            teamPMConfigurationEnabled,
            pulseConfigSetting
          };

          const fetchConfigs = [
            { fetchType: 'inProgIssues', responseKey: 'inProgIssues' },
            { fetchType: 'createdIssues', responseKey: 'createdIssues' },
            { fetchType: 'doneIssues', responseKey: 'doneIssues' }
          ];

          for (const { fetchType, responseKey } of fetchConfigs) {
            const response = await getPulseData({
              ...commonParams,
              fetchType
            });
            data.issues = [...data.issues, ...response[responseKey]];
            setRawData({ ...data });
            updateStateAfterDataChange(data);
          }

          // Fetch branches separately
          const branchesResponse = await getPulseData({
            ...commonParams,
            fetchType: 'branches'
          });
          data.unmatched = branchesResponse.unmatched;
          data.all = branchesResponse.all;
          setRawData({ ...data });
          setBranchesData({ unmatched: data.unmatched, all: data.all });
          setTableHeight('0');
          const tableData = mapDataToUiTableData(
            data,
            contributors,
            selectedContributorIds
          );
          setTableData([...tableData]);
          const peopleFilterData = mapDataToUiPeopleFilterData(
            data,
            selectedTeamContributors
          );
          setPeopleFilterData(peopleFilterData);
          TrackingService.logEvent(TRACKING.EVENTS.VISIT, {
            page: TRACKING.PAGES.PULSE,
            page_tab: TRACKING.PAGE_TABS.PULSE,
            team_name: currentTeam.name,
            total_issues: tableData.length,
            unmatched: data.unmatched.length,
            out_of: data.all.length
          });
          // Special event to mark the user is seeing a full pulse view
          TrackingService.logEvent(TRACKING.EVENTS.FULL_PULSE_VIEW);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error fetching pulse data:', error);
        } finally {
          setIsLoading(false);
        }
      };

      // Call the async function
      fetchDataSequentially();
    }
  }, 1500);

  // Helper function to update state after each data change
  const updateStateAfterDataChange = data => {
    const tableData = mapDataToUiTableData(
      data,
      contributors,
      selectedContributorIds
    );
    setTableData([...tableData]);
    const peopleFilterData = mapDataToUiPeopleFilterData(
      data,
      selectedTeamContributors
    );
    setPeopleFilterData(peopleFilterData);
  };

  useEffect(() => {
    makeApiCall();
    return makeApiCall.cancel;
  }, [
    currentIterationId,
    isPostRouteSetter,
    currentTeamId,
    teamPMConfigurationEnabled
  ]);

  const changeTableIssueProps = useCallback(
    newIssue => {
      const { id, ...issueProps } = newIssue;
      const iterationsIssues = _.get(rawData, 'issues', []);
      const issueFromRawData = iterationsIssues.find(i => i.id === id);
      if (issueFromRawData && !_.isEmpty(issueProps)) {
        Object.keys(issueProps).forEach(key => {
          issueFromRawData[key] = issueProps[key];
        });
        setRawData({ ...rawData });
      }
    },
    [rawData]
  );

  const onOpenIssueColDropdown = useCallback(
    issue => {
      setSelectedIssueDropdownState({
        issueId: issue.issueId,
        isLoading: true
      });
      getIssuePossibleStates({ id: issue.issueId })
        .then(({ current, available_states }) => {
          const didSync = current.provider_state !== issue.issueState;
          TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
            page: TRACKING.PAGES.PULSE,
            click_type: TRACKING.CLICK.CHANGE_JIRA_STATE,
            state_sync: `${didSync}`
          });
          if (current.provider_state !== issue.issueState) {
            toggleSnackBar({
              message: (
                <Text size='large' color='white'>
                  <b>{issue.issueKey}</b> was synced to its latest state:{' '}
                  <b>{current.provider_state}</b>
                </Text>
              ),
              variant: 'info'
            });
          }
          changeTableIssueProps({
            id: issue.issueId,
            issue_state: current.issue_state,
            provider_state: current.provider_state
          });
          setSelectedIssueDropdownState({
            issueId: issue.issueId,
            isLoading: false,
            isOpen: true,
            possibleStates: available_states
          });
        })
        .catch(() => {
          setSelectedIssueDropdownState({});
          toggleSnackBar({
            message: (
              <Text size='large' color='white'>
                Failed to get available issue states
              </Text>
            ),
            variant: 'error'
          });
        });
    },
    [changeTableIssueProps, toggleSnackBar]
  );

  const onCloseIssueColDropdown = useCallback(
    () => setSelectedIssueDropdownState({}),
    [setSelectedIssueDropdownState]
  );

  const selectIssueState = useCallback(
    ({ issue, state: { state_name, state_id } }) => {
      setSelectedIssueDropdownState({
        issueId: issue.issueId,
        isLoading: true
      });
      patchIssueState({ id: issue.issueId, state_id, state_name })
        .then(({ issue_state, provider_state }) => {
          setAllowAnimations(true);
          changeTableIssueProps({
            id: issue.issueId,
            issue_state,
            provider_state
          });
          toggleSnackBar({
            message: (
              <Text size='large' color='white'>
                Status successfully updated on your Jira board
              </Text>
            ),
            variant: 'success'
          });
          TrackingService.logEvent(TRACKING.EVENTS.FILTER, {
            page: TRACKING.PAGES.PULSE,
            filter_name: TRACKING.FILTER.JIRA_STATE_CHANGED,
            filter_value: issue_state,
            state_change_success: 'true'
          });
        })
        .catch(() => {
          toggleSnackBar({
            message: (
              <Text size='large' color='white'>
                We couldn't update the issue status. make sure all pre
                conditions are met on Jira
              </Text>
            ),
            variant: 'error'
          });
          TrackingService.logEvent(TRACKING.EVENTS.FILTER, {
            page: TRACKING.PAGES.PULSE,
            filter_name: TRACKING.FILTER.JIRA_STATE_CHANGED,
            state_change_success: 'false'
          });
        })
        .finally(() =>
          setSelectedIssueDropdownState({
            issueId: issue.issueId,
            isLoading: false,
            possibleStates: []
          })
        );
    },
    [changeTableIssueProps, toggleSnackBar]
  );

  useEffect(() => {
    if (!notConnectedType) {
      setTopBarData(
        getTopBarData(tableData, issueTypeFilterValues, pmSensor.sensor_id)
      );
    }
  }, [tableData, issueTypeFilterValues]);

  useEffect(() => {
    if (notConnectedType) {
      TrackingService.logEvent(TRACKING.EVENTS.VISIT, {
        page: TRACKING.PAGES.PULSE,
        page_tab: notConnectedType.toLowerCase()
      });
    }
  }, []);

  const onSearch = value => {
    setAllowAnimations(false);
    setSearchFilterInitValue(null);
    _.debounce(value => {
      setSearchFilterValue(value);
      if (value) {
        TrackingService.logEvent(TRACKING.EVENTS.FILTER, {
          page: TRACKING.PAGES.PULSE,
          filter_name: TRACKING.FILTER.SEARCH,
          team_name: currentTeam.name
        });
      }
    }, 400)(value);
  };

  const data = useMemo(
    () =>
      _.orderBy(
        tableData,
        ['latestActivity', 'totalCodeChanges'],
        ['desc', 'desc']
      ).map(item => {
        const isIssueSelected =
          item.issueId === selectedIssueDropdownState.issueId;
        const iconSrc =
          item.iconUrl ||
          getIssueTypeIconByPmProvider(pmSensor.sensor_id, item.issueType);

        return {
          key: item.issueId,
          filtersData: {
            state: item.jiraIssueState,
            type: item.issueType,
            name: item.issueName,
            key: item.issueKey,
            contributors: item.contributors
          },
          issues: (
            <IssueCol
              issueState={
                item.jiraIssueState !== 'created' && item.jiraIssueState
              }
              iconUrl={iconSrc}
              name={item.issueName}
              tag={item.issueState}
              jiraLink={item.jiraLink}
              issueId={item.issueId}
              issueKey={item.issueKey}
              issueType={item.issueType}
              isOpen={isIssueSelected && selectedIssueDropdownState.isOpen}
              storyPoints={item.storyPoints}
              avatars={item.avatars}
              assignees={item.assignees}
              possibleStates={
                isIssueSelected && selectedIssueDropdownState.possibleStates
              }
              isLoading={
                isIssueSelected && selectedIssueDropdownState.isLoading
              }
              shadowWork={item.shadow_work}
              onCloseDropdown={() => onCloseIssueColDropdown()}
              onOpenDropdown={() => onOpenIssueColDropdown(item)}
              onSelectState={state => selectIssueState({ issue: item, state })}
              withIssueStateDropdown={pmSensor.sensor_id === PM_PROVIDERS.JIRA}
            />
          ),
          contributors: <MultipleAvatars avatars={item.avatars} />,
          codeChanges: (
            <CodeChangesCol
              changes={item.totalCodeChanges}
              reposInfo={item.reposInfo}
              activeBranches={item.activeCount}
            />
          ),
          activity: (
            <ActivityCol
              active={item.activeCount}
              merged={item.mergedCount}
              released={item.deployedCount}
            />
          ),
          timeline: (
            <IterationGraph
              carryover={item.carryOver}
              items={item.graphItems}
              hasAlerts={_.get(item, 'alerts', []).length}
            />
          ),
          stuck: <StuckCol alerts={item.alerts} />,
          ...(item.jiraIssueState === 'done' ? { styles: doneRowStyles } : {})
        };
      }),
    [
      tableData,
      selectIssueState,
      onOpenIssueColDropdown,
      onCloseIssueColDropdown,
      selectedIssueDropdownState
    ]
  );

  const filteredData = data.filter(filterData) || [];
  const filteredDataWithoutContributors =
    data.filter(filterDataWithoutContributors) || [];
  const filteredBranchesByContributor = branches =>
    (branches || []).filter(branch =>
      branch.contributor_ids.some(
        id =>
          !selectedContributorIds.length || selectedContributorIds.includes(id)
      )
    );

  const filterBranchesByIssueState = branches =>
    (branches || []).filter(
      branch =>
        issueStateFilterValues.length === 0 ||
        mapIssueStatesToBranchStates(issueStateFilterValues).includes(
          branch.state
        )
    );

  const filterBranchesBySearch = branches =>
    (branches || []).filter(branch =>
      branch.name.toLowerCase().includes(searchFilterValue)
    );

  const filterUnmatchedBranchesByDynamicData = branches => {
    const dynamicBranches = _.get(rawData, 'issues', [])?.reduce(
      (acc, issue) =>
        acc.add(...(issue.branches || []).map(branch => branch.id)),
      new Set()
    );
    return (branches || []).filter(branch => !dynamicBranches?.has(branch.id));
  };

  branchesData.unmatched = filterUnmatchedBranchesByDynamicData(
    filterBranchesBySearch(
      filterBranchesByIssueState(
        filteredBranchesByContributor(rawData.unmatched)
      )
    )
  );

  branchesData.all = filterBranchesBySearch(
    filterBranchesByIssueState(filteredBranchesByContributor(rawData.all))
  );

  const pulseActiveBranches =
    tableData.reduce((acc, item) => acc + item.activeCount, 0) +
    branchesData.unmatched.length;

  const showNoIssues =
    !filteredData.length && branchesData.unmatched.length > 0;

  const showEmptyState =
    !isLoading &&
    (!data.length || !filteredData.length) &&
    branchesData.unmatched.length === 0;

  const showFilterPeopleWithNoIssues =
    selectedContributorIds.length === 1 &&
    !filteredData.length &&
    branchesData.unmatched.length > 0;

  const showTable = isLoading || (!showEmptyState && filteredData.length > 0);

  return (
    <WrappedComponent
      data={data}
      filteredData={filteredData}
      filteredDataWithoutContributors={filteredDataWithoutContributors}
      tableHeight={tableHeight}
      workingDays={workingDays}
      branchesData={branchesData}
      allowAnimations={allowAnimations}
      isLoading={isLoading}
      selectedContributorIds={selectedContributorIds}
      peopleFilterData={peopleFilterData}
      topBarData={topBarData}
      latestIteration={currentIteration}
      notConnectedType={notConnectedType}
      issueStateFilterValues={issueStateFilterValues}
      setSelectedContributorIds={setSelectedContributorIds}
      setAllowAnimations={setAllowAnimations}
      setTableHeight={setTableHeight}
      onSearch={onSearch}
      issueTypeFilterValues={issueTypeFilterValues}
      searchFilterInitValue={searchFilterInitValue}
      setPeopleFilterData={setPeopleFilterData}
      setIssueTypeFilterValues={setIssueTypeFilterValues}
      setSearchFilterValue={setSearchFilterValue}
      setSearchFilterInitValue={setSearchFilterInitValue}
      setIssueStateFilterValues={setIssueStateFilterValues}
      showEmptyState={showEmptyState}
      showFilterPeopleWithNoIssues={showFilterPeopleWithNoIssues}
      showTable={showTable}
      showNoIssues={showNoIssues}
      pulseActiveBranches={pulseActiveBranches}
      {...props}
    />
  );
};

export default withPulseData;
